import React, { useState, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet } from '../services/userDataService'
import { legRecord } from '../TRACKER_PAGE/settlement'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { subjectFor, SubjectAvatar, legMarketLabel } from '../TRACKER_PAGE/LiveBetMeter'
import { willAutoSettle } from './trackFromOdds'

// ============================================================================
// Odds screen betslip.
//
// A price on the odds table is a bet you might take, so clicking one puts it in
// a slip rather than interrupting with a modal. The screen exists to COMPARE
// prices; a dialog on every click fights the one thing the page is for.
//
// The slip is also what makes multis fall out for free. A multi is not a second
// feature here, it is the same list of selections submitted a different way,
// which is why singles and multis share one component.
//
// ONE file for both platforms. `compact` tightens it for a phone; everything
// else — the rules, the maths, the save — is identical, so a multi built on a
// phone is the same object as one built on a desktop.
//
// No EV field anywhere. These bets come off the odds table with no edge
// calculated, and an empty input would invite someone to type a number they
// have not measured. They save with ev_percentage null and the tracker keeps
// them out of its expected-profit figures entirely.
// ============================================================================

const ACCENT = '#22d3ee'          // the odds-screen colour, matching the tracker badge
const PANEL_BG = '#0b1322'
const BORDER = '#1b2a45'

/** Two selections are the same bet if they name the same outcome at the same book. */
export const slipKey = (opp) =>
  `${opp?.match}|${opp?.market_type}|${opp?.selection}|${opp?.bookmaker}`

/**
 * Whether these legs can be a multi at all.
 *
 * ONE BOOKMAKER. A multi is a single wager placed at one book, so legs priced
 * at different books cannot be combined — the combined price would not exist
 * anywhere. This is the same rule the EV auto-builder enforces.
 */
function multiBlocker(legs) {
  if (legs.length < 2) return 'Add another selection to build a multi'
  const books = new Set(legs.map(l => l.bookmaker))
  if (books.size > 1) {
    return `All legs must be at one bookmaker (you have ${books.size})`
  }
  return null
}

/**
 * Legs sharing a fixture, which books price with CORRELATION rather than as
 * independent events.
 *
 * Multiplying the legs is then wrong, usually generously so: two outcomes from
 * one game reinforce each other, and the book's real same-game price is far
 * shorter than the product. The slip still allows it, because a same-game multi
 * is a normal thing to want, but it says so and asks for the price actually on
 * offer rather than quietly recording a number the user was never given.
 */
function sameGameLegs(legs) {
  const seen = new Map()
  for (const leg of legs) {
    const key = `${leg.match}|${leg.commence_time}`
    seen.set(key, (seen.get(key) ?? 0) + 1)
  }
  return [...seen.values()].some(n => n > 1)
}

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`

/**
 * The image identifying a selection — the player's face, or the crest(s).
 *
 * subjectFor() wants the teams as separate fields; an odds-screen opportunity
 * carries them joined as `match`, the same " vs " the settler splits on.
 */
const subjectForLeg = (leg) => subjectFor({
  sport:       leg?.sport,
  player_name: leg?.player_name,
  team_name:   leg?.team_name,
  home_team:   leg?.match?.split(' vs ')[0],
  away_team:   leg?.match?.split(' vs ')[1],
})

/**
 * A quiet marker that the number beside it is derived rather than quoted.
 *
 * Uses the app's shared `[data-tooltip]` styling rather than a native title, so
 * it matches every other hint in the product. That tooltip is `nowrap`, and the
 * panel it sits in clips its overflow, so the text has to stay short enough to
 * fit inside the slip's width.
 */
function InfoDot({ tooltip }) {
  return (
    <span
      className="odds-slip-info"
      data-tooltip={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '13px', height: '13px', borderRadius: '50%',
        border: '1px solid #475569', color: '#94a3b8',
        fontSize: '9px', fontWeight: 700, fontStyle: 'italic',
        cursor: 'help', flex: '0 0 auto', lineHeight: 1
      }}
    >
      i
    </span>
  )
}

/**
 * @param legs        selections currently in the slip (opportunity shape)
 * @param onRemove    remove one, by slipKey
 * @param onClear     empty the slip
 * @param onSaved     called after a successful save, so the page can confirm
 * @param compact     phone layout
 * @param singlesOnly hide the Singles / Multi switch (Racing Odds: singles only)
 * @param renderAvatar optional (leg) => image for a selection, in place of the
 *                    tracker's player/crest lookup (Racing Odds passes the silk)
 * @param betTag      bet_type_tag for singles ('racing_odds' from Racing Odds)
 * @param extraFields optional (leg) => extra fields saved on that single
 */
export default function OddsBetslip({ legs, onRemove, onClear, onOddsChange, onSaved, compact = false, singlesOnly = false, renderAvatar = null, betTag = 'odds_screen', extraFields = null }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('singles')     // 'singles' | 'multi'
  const [stakes, setStakes] = useState({})        // slipKey -> stake, singles mode
  const [multiStake, setMultiStake] = useState('')
  const [multiOdds, setMultiOdds] = useState('')  // manual override, same-game especially
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const blocker = multiBlocker(legs)
  const correlated = sameGameLegs(legs)

  // Product of the legs. Honest for legs in different games; an overstatement
  // for legs in the same one, which is what the warning below is about.
  const productOdds = useMemo(
    () => legs.reduce((total, leg) => total * (Number(leg.odds) || 1), 1),
    [legs]
  )

  const effectiveMultiOdds = (() => {
    const typed = parseFloat(multiOdds)
    return Number.isFinite(typed) && typed > 1 ? typed : productOdds
  })()

  if (legs.length === 0) return null

  // ── Collapsed ─────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        className="odds-slip-pill"
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          right: compact ? '12px' : '24px',
          bottom: compact ? '76px' : '24px',
          zIndex: 60,
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          padding: '11px 16px',
          borderRadius: '999px',
          border: `1px solid ${ACCENT}`,
          backgroundColor: PANEL_BG,
          color: ACCENT,
          fontSize: '13px',
          fontWeight: 700,
          letterSpacing: '0.02em',
          cursor: 'pointer'
        }}
      >
        Betslip
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '20px', height: '20px', padding: '0 6px',
          borderRadius: '999px', backgroundColor: ACCENT, color: '#04222a',
          fontSize: '11px', fontWeight: 800
        }}>
          {legs.length}
        </span>
      </button>
    )
  }

  const save = async () => {
    if (!user?.id) { setError('Please log in to track bets'); return }
    setError(null)
    setSaving(true)
    try {
      const now = new Date().toISOString()

      if (mode === 'multi') {
        if (blocker) { setError(blocker); return }
        const stake = parseFloat(multiStake)
        if (!Number.isFinite(stake) || stake <= 0) { setError('Enter a stake'); return }
        // A leg left blank reads as 1.00 through the product, quietly shortening
        // the multi instead of failing, so it is caught here rather than saved.
        if (legs.some(l => !Number.isFinite(parseFloat(l.odds)) || parseFloat(l.odds) <= 1)) {
          setError('Every leg needs odds above 1.00')
          return
        }

        const bet = {
          type: 'EV',
          bet_type_tag: 'odds_screen_multi',
          sport: legs[0].sport,
          match: `${legs.length}-Leg Multi`,
          selection: legs.map(l => l.selection).join(' / '),
          bookmaker: legs[0].bookmaker,
          market_type: 'multi',
          // The structured fields per leg are what let settlement resolve a
          // multi one game at a time. Without them it is only a sentence.
          legs: legs.map(legRecord),
          odds: parseFloat(effectiveMultiOdds.toFixed(2)),
          stake,
          ev_percentage: null,
          status: 'pending',
          actual_profit: null,
          placed_at: now,
          settled_at: null,
        }
        const { error: err } = await saveUserBet(user.id, bet)
        if (err) { setError('Could not save. Try again.'); return }

      } else {
        // Each selection saved as its own bet, which is why legs at different
        // books are fine here and refused for a multi.
        //
        // Odds are parsed rather than taken as-is: they are editable, so they
        // arrive as whatever the user has typed so far. A string would settle
        // as a loss on a WON bet, since payout() reads (odds - 1) * stake.
        const entries = legs.map(leg => ({
          leg,
          stake: parseFloat(stakes[slipKey(leg)]),
          odds:  parseFloat(leg.odds),
        }))
        if (entries.some(e => !Number.isFinite(e.stake) || e.stake <= 0)) {
          setError('Enter a stake for every selection')
          return
        }
        if (entries.some(e => !Number.isFinite(e.odds) || e.odds <= 1)) {
          setError('Every selection needs odds above 1.00')
          return
        }

        for (const { leg, stake, odds } of entries) {
          const bet = {
            type: 'EV',
            bet_type_tag: betTag,
            sport: leg.sport,
            match: leg.match,
            selection: leg.selection,
            bookmaker: leg.bookmaker,
            market_type: leg.market_type,
            commence_time: leg.commence_time,
            // Straight off the adapter, so these settle by the same rules an EV
            // single does.
            base_market_type: leg.base_market_type,
            side: leg.side,
            point: leg.point,
            player_name: leg.player_name,
            team_name: leg.team_name,
            home_team: leg.match?.split(' vs ')[0] || null,
            away_team: leg.match?.split(' vs ')[1] || null,
            odds,
            stake,
            ev_percentage: null,
            bookmaker_count: null,
            status: 'pending',
            actual_profit: null,
            placed_at: now,
            settled_at: null,
            ...(extraFields ? extraFields(leg) : {}),
          }
          const { error: err } = await saveUserBet(user.id, bet)
          if (err) { setError('Could not save. Try again.'); return }
        }
      }

      setStakes({})
      setMultiStake('')
      setMultiOdds('')
      onClear?.()
      setOpen(false)
      onSaved?.(mode, legs.length)
    } finally {
      setSaving(false)
    }
  }

  const label = { fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#64748b' }
  const input = {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: '7px',
    border: `1px solid ${BORDER}`, backgroundColor: '#101b30',
    color: '#e2e8f0', fontSize: '13px', fontVariantNumeric: 'tabular-nums'
  }

  const unsettleable = legs.filter(l => !willAutoSettle(l.market_type))

  // ── Expanded ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        right: compact ? '8px' : '24px',
        bottom: compact ? '72px' : '24px',
        left: compact ? '8px' : 'auto',
        width: compact ? 'auto' : '420px',
        maxHeight: compact ? '70vh' : '78vh',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 60,
        borderRadius: '14px',
        border: `1px solid ${BORDER}`,
        backgroundColor: PANEL_BG,
        boxShadow: '0 18px 44px rgba(0,0,0,0.5)',
        // NOT clipped. The legs list does its own scrolling, so the only thing
        // `overflow: hidden` was containing here was the tooltip, which has to
        // be able to leave the panel to be readable at this width.
        overflow: 'visible'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderBottom: `1px solid ${BORDER}` }}>
        {/* No count here. The mode buttons directly below already say "3
            Singles", so a "3 selected" beside the title was the same fact
            twice, two lines apart. */}
        <span style={{ fontSize: '13px', fontWeight: 800, color: '#f1f5f9', letterSpacing: '0.01em' }}>Betslip</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          title="Hide betslip"
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}
        >
          ×
        </button>
      </div>

      {/* Mode. Explicit rather than inferred from the count: two selections at
          two different books are two singles, not a broken multi, and guessing
          would silently pick the wrong one. */}
      {!singlesOnly && (
      <div style={{ display: 'flex', gap: '6px', padding: '10px 14px 0' }}>
        {[
          { key: 'singles', text: legs.length === 1 ? 'Single' : `${legs.length} Singles` },
          { key: 'multi', text: 'Multi' },
        ].map(({ key, text }) => {
          const active = mode === key
          const disabled = key === 'multi' && !!blocker
          return (
            <button
              key={key}
              type="button"
              onClick={() => !disabled && setMode(key)}
              title={disabled ? blocker : undefined}
              style={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: '8px',
                fontSize: '11.5px',
                fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                border: `1px solid ${active ? ACCENT : BORDER}`,
                backgroundColor: active ? 'rgba(34,211,238,0.12)' : 'transparent',
                color: active ? ACCENT : '#94a3b8'
              }}
            >
              {text}
            </button>
          )
        })}
      </div>
      )}

      {/* Legs */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
        {legs.map((leg, legIdx) => {
          const isLast = legIdx === legs.length - 1
          const key = slipKey(leg)
          const subject = renderAvatar ? null : subjectForLeg(leg)
          const avatar = renderAvatar ? renderAvatar(leg) : null
          return (
            <div key={key} style={{ padding: '9px 0', borderBottom: isLast ? 'none' : '1px solid #16233c' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                {/* Same images the tracker draws, so a selection looks the same
                    here as it will once it is a bet. Skipped when the market has
                    none rather than holding an empty circle: one column of legs
                    has no other rows to keep a straight edge against. */}
                {subject && (
                  <div className="odds-slip-sub" style={{ flex: '0 0 auto' }}>
                    <SubjectAvatar subject={subject} />
                  </div>
                )}
                {avatar && <div style={{ flex: '0 0 auto' }}>{avatar}</div>}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    title={leg.selection}
                    style={{
                      fontSize: '12.5px', fontWeight: 700, color: '#f1f5f9', lineHeight: 1.3,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}
                  >
                    {leg.selection}
                  </div>
                  {/* On the odds table the market comes from the dropdown
                      above and every row shares it; in a slip that can hold two
                      different markets at once, the row has to name its own. */}
                  {legMarketLabel(leg) && (
                    <div style={{ fontSize: '10.5px', color: '#94a3b8', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {legMarketLabel(leg)}
                    </div>
                  )}

                  <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {leg.match}
                  </div>
                </div>

                {/* Logo FIRST and the price a fixed width, so a 1.90 and a 10.50
                    put their logo in the same place. Left to size itself, the
                    group's width tracked the number and every logo sat on its
                    own x. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flex: '0 0 auto' }}>
                  {/* The book's own page for this fixture, so the slip is a
                      place to go and PLACE the bets rather than only record
                      them. Not a link when the feed sent no url, rather than a
                      dead one. */}
                  {BOOKMAKER_LOGOS[leg.bookmaker]
                    ? <img
                        src={BOOKMAKER_LOGOS[leg.bookmaker]}
                        alt={leg.bookmaker}
                        title={leg.game_url ? `Open ${leg.bookmaker}` : leg.bookmaker}
                        onClick={() => leg.game_url && window.open(leg.game_url, '_blank', 'noopener')}
                        style={{ width: '22px', height: '22px', borderRadius: '5px', objectFit: 'contain', flex: '0 0 auto', cursor: leg.game_url ? 'pointer' : 'default' }}
                      />
                    : <span style={{ fontSize: '10px', color: '#94a3b8', width: '22px', flex: '0 0 auto', textAlign: 'center' }}>{leg.bookmaker.slice(0, 2)}</span>}

                  {/* Editable: the price on screen is a snapshot, and the one
                      actually taken is often a tick different by the time the
                      bet is placed. */}
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={leg.odds ?? ''}
                    onChange={(e) => onOddsChange?.(key, e.target.value)}
                    title="Odds taken"
                    style={{
                      width: '58px',
                      flex: '0 0 auto',
                      boxSizing: 'border-box',
                      padding: '4px 6px',
                      borderRadius: '6px',
                      border: `1px solid ${BORDER}`,
                      backgroundColor: '#101b30',
                      color: ACCENT,
                      fontSize: '12.5px',
                      fontWeight: 800,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => onRemove?.(key)}
                    title="Remove"
                    style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px', flex: '0 0 auto' }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {mode === 'singles' && (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Stake"
                  value={stakes[key] ?? ''}
                  onChange={(e) => setStakes(prev => ({ ...prev, [key]: e.target.value }))}
                  style={{ ...input, marginTop: '7px' }}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 14px', borderTop: `1px solid ${BORDER}` }}>
        {mode === 'multi' && (
          <>
            {blocker ? (
              <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '9px' }}>{blocker}</div>
            ) : (
              <>
                {/* minWidth 0 on both columns: a flex item defaults to its
                    content's minimum width, so two padded inputs at width 100%
                    each demanded more than half and the boxes overlapped. */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '9px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...label, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>Combined odds</span>
                      {/* Same-game legs are priced with correlation, so the
                          product is not the price on offer. A marker on the
                          field it applies to, rather than a banner: the field
                          is where the correction gets made. */}
                      {correlated && (
                        <InfoDot tooltip="Legs from the same game are priced together, so this is only an estimate. Enter the price your bookmaker shows." />
                      )}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      placeholder={productOdds.toFixed(2)}
                      value={multiOdds}
                      onChange={(e) => setMultiOdds(e.target.value)}
                      style={input}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...label, marginBottom: '4px' }}>Stake</div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={multiStake}
                      onChange={(e) => setMultiStake(e.target.value)}
                      style={input}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: '#94a3b8', marginBottom: '10px' }}>
                  <span>Potential return</span>
                  <span style={{ fontWeight: 700, color: '#e2e8f0', fontVariantNumeric: 'tabular-nums' }}>
                    {money((parseFloat(multiStake) || 0) * effectiveMultiOdds)}
                  </span>
                </div>
              </>
            )}
          </>
        )}

        {unsettleable.length > 0 && (
          // Correct score has no resolver, so it will sit pending until settled
          // by hand. Better said now than discovered in the tracker.
          <div style={{ fontSize: '10.5px', lineHeight: 1.45, color: '#94a3b8', marginBottom: '9px' }}>
            {unsettleable.length === 1 ? 'One selection' : `${unsettleable.length} selections`} cannot settle automatically and will need marking by hand.
          </div>
        )}

        {error && <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '9px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={onClear}
            style={{
              // Clear glass, matching the page's other secondary buttons.
              padding: '9px 12px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600,
              border: '1px solid rgba(148, 163, 184, 0.16)', backgroundColor: 'rgba(148, 163, 184, 0.03)',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)', color: '#cbd5e1', cursor: 'pointer'
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || (mode === 'multi' && !!blocker)}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 800,
              border: 'none', backgroundColor: ACCENT, color: '#04222a',
              cursor: saving || (mode === 'multi' && !!blocker) ? 'not-allowed' : 'pointer',
              opacity: saving || (mode === 'multi' && !!blocker) ? 0.5 : 1
            }}
          >
            {saving ? 'Saving…' : mode === 'multi' ? 'Track multi' : `Track ${legs.length === 1 ? 'bet' : `${legs.length} bets`}`}
          </button>
        </div>
      </div>
    </div>
  )
}
